/**
 * The bakes of one sky, one for each exposure that bakes it. Godot bakes a sky's radiance at the
 * exposure of the render that finds it dirty (`sky.cpp:1275`, `:1295`), and a sky that reads lights
 * is dirty in each render whose light energies, which the exposure scales, differ from the last
 * (`sky.cpp:1085-1087`, `:1105-1143`). So viewports that draw one sky at two exposures can read two
 * bakes. A bake holds the exposure only in its light energies and its debanding noise.
 */
import { buildSkyEnvironment } from '../../resources/sky/build';
import { roughnessLayersOf } from '../../resources/sky/skyLayers';
import { materialReadsLights } from '../../resources/sky/readsLights';
import { isDebanded } from '../../resources/sky/debanding';
import { newSkyRadianceUpdate, skyUpdateMode, } from '../../godot/rendering/environment/skyRadianceUpdate';
import { perObject } from '../../utils/perObject';
import { holdUntilReleased } from '../useDisposeOnRelease';
import { SkyRadiance } from './skyRadiance';
import { isLiveTarget, liveTargetDrawCount } from '../liveTargets';
/** How `sky`'s radiance updates. */
export function skyUpdatePlan(sky) {
    return { mode: skyUpdateMode(sky.processMode), layers: roughnessLayersOf(sky.radianceFilter) };
}
/**
 * The radiance update of each sky, which every Environment that draws the sky shares, as Godot keeps
 * one radiance for each Sky resource (`sky.h:251`). A new material is a new sky, which bakes anew.
 */
export const radianceUpdateOf = perObject((_sky) => newSkyRadianceUpdate());
/**
 * The bakes each renderer holds of each sky, by the rest of the input. Written only by
 * `holdSkyBakes`, and an entry leaves when its bakes are disposed.
 */
const heldBakesOf = perObject((_gl) => perObject((_sky) => new Map()));
/**
 * The bakes of `input` on `gl`, which every layer that draws the same sky with the same input
 * shares, as Godot shares one radiance. `lightsKey` names the lights, and `contextRestores` the
 * context the bakes live in. The caller owns the release.
 */
export function holdSkyBakes(gl, input, lightsKey, contextRestores) {
    const held = heldBakesOf(gl)(input.sky);
    const key = `${inputKey(input, lightsKey)}|${contextRestores}`;
    const bakes = held.get(key) ?? new SkyBakes(gl, input, () => held.delete(key));
    held.set(key, bakes);
    return { bakes, release: holdUntilReleased(bakes) };
}
/** The input a bake reads besides the sky. Only a debanded sky reads the energy multiplier. */
function inputKey({ sky, panorama, asBackground, energyMultiplier, rotation }, lightsKey) {
    const debandedEnergy = isDebanded(sky.material) ? energyMultiplier : '';
    return [
        panorama?.uuid ?? '',
        asBackground,
        debandedEnergy,
        rotation.x,
        rotation.y,
        rotation.z,
        lightsKey,
    ].join('|');
}
/** The one exposure a bake that holds none bakes at, as every exposure would bake it alike. */
const ANY_EXPOSURE = 1;
/**
 * The bakes of one input by exposure. Godot keeps one radiance for a sky, so a bake that no render
 * read since the last sweep is freed, and a render whose bake waits draws with the latest one.
 */
export class SkyBakes {
    gl;
    input;
    onDispose;
    /** Each bake by its exposure, null for one the renderer could not make. */
    bakes = new Map();
    /** The exposures a render asked for while it drew, which `bakeWanted` bakes. */
    wanted = new Set();
    /** The exposures read since the last sweep, written by `at` and `bakeNow`, cleared by each sweep. */
    read = new Set();
    /** The bake read last, which a render whose bake waits draws with, and which no sweep frees. */
    latest = null;
    /** Whether the exposure changes a bake: through a light energy, or the debanding noise. */
    holdsExposure;
    /** Grows by one with each bake made, so a reader tells a new one. */
    bakeCount = 0;
    /**
     * The draw of a live panorama the backgrounds show, written only by `followPanorama`. Null for a
     * panorama no pass draws.
     */
    backgroundDraws;
    constructor(gl, input, onDispose) {
        this.gl = gl;
        this.input = input;
        this.onDispose = onDispose;
        const { sky, lights, panorama } = input;
        const exposureScalesLights = materialReadsLights(sky.material) && lights.some((light) => light.energy !== 0);
        this.holdsExposure = exposureScalesLights || isDebanded(sky.material);
        this.backgroundDraws = panorama && isLiveTarget(panorama) ? liveTargetDrawCount(panorama) : null;
    }
    /** Bakes the sky at `exposure` now, outside any render, or answers the bake it holds. */
    bakeNow(exposure) {
        const key = this.keyOf(exposure);
        if (!this.bakes.has(key))
            this.bake(key);
        return this.readOf(key);
    }
    /**
     * The bake at `exposure`, else the latest while it waits for `bakeWanted`, since a bake renders
     * cubes of its own. Null only before any bake.
     */
    at(exposure) {
        const key = this.keyOf(exposure);
        if (!this.bakes.has(key))
            this.wanted.add(key);
        return this.readOf(key) ?? (this.latest && this.readOf(this.latest.exposure));
    }
    /**
     * Frees each bake no render read since the last sweep, then bakes each exposure a render asked
     * for. Answers whether it baked any. A sweep with no read between runs nothing, so several
     * holders can call this in one frame.
     */
    bakeWanted() {
        if (this.read.size > 0)
            this.sweep();
        if (this.wanted.size === 0)
            return false;
        for (const exposure of this.wanted)
            this.bake(exposure);
        this.wanted.clear();
        return true;
    }
    /**
     * Draws the background of each bake again once per new draw of a live panorama, as Godot draws
     * the background from the sky shader each frame (`sky.cpp:1463`). The radiance keeps its bake, as
     * a draw of the target leaves Godot's sky clean. Every layer that shares these bakes may call it.
     */
    followPanorama() {
        const { panorama } = this.input;
        if (this.backgroundDraws === null || !panorama)
            return;
        const draws = liveTargetDrawCount(panorama);
        if (draws === this.backgroundDraws)
            return;
        this.backgroundDraws = draws;
        for (const baked of this.bakes.values())
            baked?.redrawBackground?.();
    }
    dispose() {
        for (const baked of this.bakes.values())
            free(baked);
        this.onDispose();
    }
    bake(exposure) {
        const baked = bakeSky(this.gl, this.input, exposure);
        this.bakes.set(exposure, baked);
        this.read.add(exposure);
        if (baked)
            this.bakeCount++;
    }
    readOf(exposure) {
        const baked = this.bakes.get(exposure) ?? null;
        if (!baked)
            return null;
        this.read.add(exposure);
        this.latest = { exposure, baked };
        return baked;
    }
    sweep() {
        for (const [exposure, baked] of this.bakes) {
            if (this.read.has(exposure) || exposure === this.latest?.exposure)
                continue;
            free(baked);
            this.bakes.delete(exposure);
        }
        this.read.clear();
    }
    keyOf(exposure) {
        return this.holdsExposure ? exposure : ANY_EXPOSURE;
    }
}
function free(baked) {
    baked?.radiance.hide();
    baked?.dispose();
}
/** The sky baked at `exposure`, which scales the energy of each light and the debanding noise. */
function bakeSky(gl, input, exposure) {
    const { energyMultiplier, rotation, lights, ...sky } = input;
    const energy = energyMultiplier * exposure;
    const built = buildSkyEnvironment(gl, { ...sky, lights: exposed(lights, exposure), energy });
    if (!built)
        return null;
    const radiance = new SkyRadiance(gl, built.layers, rotation);
    const { background, redrawBackground, dispose } = built;
    return { background, radiance, redrawBackground, dispose };
}
function exposed(lights, exposure) {
    return lights.map((light) => ({ ...light, energy: light.energy * exposure }));
}
//# sourceMappingURL=skyBakes.js.map